import { Avatar, ChakraBox, ChakraFlex, ChakraText } from '@equip.health/ui';

import {
  getAgeFromDOB,
  getDiffInWeeks,
  getPreferredFullName,
} from '~/lib/util/patient.util';
import { COLOR_LEVELS } from '../constants';
import { PatientProviderDashboard } from '~/store/services/providerDashboard';

const DATE_FORMAT = 'yyyy-MM-dd';
const TRANSITION_TRACK = 'Transition Track';

export const PatientNameLine = ({
  fullName,
  patientExternalId,
  pronounsDisplay,
  age,
}: {
  fullName: string;
  patientExternalId: string;
  pronounsDisplay: string;
  age: number;
}) => (
  <ChakraFlex alignItems="baseline" gap="6px" flexWrap="wrap">
    <ChakraText
      data-testid={`patient-name-${patientExternalId}`}
      fontSize="14px"
      fontWeight="500"
      color="neutral.primary"
    >
      {fullName}
    </ChakraText>
    {(pronounsDisplay || !!age) && (
      <ChakraText fontSize="13px" color="neutral.secondary">
        ({[pronounsDisplay, age ? `${age}yr` : ''].filter(Boolean).join(', ')})
      </ChakraText>
    )}
  </ChakraFlex>
);

export const PatientMetaLine = ({
  level,
  weekAtEquip,
  diagnosis,
  treatmentModality,
  programName,
}: {
  level: { label: string; hex: string } | undefined;
  weekAtEquip: number;
  diagnosis: string;
  treatmentModality: string;
  programName: string;
}) => {
  const segments: React.ReactNode[] = [
    level && (
      <ChakraFlex key="level" alignItems="center" gap="4px">
        <ChakraBox
          width="10px"
          height="10px"
          borderRadius="2px"
          bg={level.hex}
          flexShrink={0}
        />
        <ChakraText fontSize="12px" color="neutral.secondary">
          {level.label}
        </ChakraText>
      </ChakraFlex>
    ),
    weekAtEquip > 0 && (
      <ChakraText key="week" fontSize="12px" color="neutral.secondary">
        Week {weekAtEquip}
      </ChakraText>
    ),
    (diagnosis || treatmentModality) && (
      <ChakraText key="dx" fontSize="12px" color="neutral.secondary">
        {diagnosis && treatmentModality
          ? `${diagnosis} (${treatmentModality})`
          : diagnosis || treatmentModality}
      </ChakraText>
    ),
    programName === TRANSITION_TRACK && (
      <ChakraText key="program" fontSize="12px" color="neutral.secondary">
        {TRANSITION_TRACK}
      </ChakraText>
    ),
  ].filter(Boolean);

  if (!segments.length) return null;

  return (
    <ChakraFlex alignItems="center" gap="6px" flexWrap="wrap" marginTop="2px">
      {segments.map((segment, i) => (
        <ChakraFlex key={i} alignItems="center" gap="6px">
          {i > 0 && (
            <ChakraText fontSize="12px" color="neutral.tertiary">
              ·
            </ChakraText>
          )}
          {segment}
        </ChakraFlex>
      ))}
    </ChakraFlex>
  );
};

/**
 * Avatar + name line + meta line for a dashboard patient. Shared by the patient
 * filter rows and the today's-sessions appointment card. An optional `footer`
 * renders inside the identity column below the meta line (e.g. a pause detail).
 */
const PatientIdentity = ({
  patient,
  footer,
  onClick,
}: {
  patient: PatientProviderDashboard;
  footer?: React.ReactNode;
  onClick?: () => void;
}) => {
  const {
    patientExternalId,
    firstName,
    lastName,
    preferredName,
    dateOfBirth,
    pronouns,
    admitDate,
    treatmentModality,
    diagnosis,
    colorLevel,
    programName,
  } = patient;

  const fullName = getPreferredFullName(preferredName, firstName, lastName);
  const age = getAgeFromDOB(dateOfBirth, DATE_FORMAT);
  const weekAtEquip = getDiffInWeeks(admitDate, undefined, DATE_FORMAT);
  const level = COLOR_LEVELS[colorLevel?.toLowerCase()];
  const pronounsDisplay = pronouns?.length ? pronouns.join(', ') : '';

  return (
    <ChakraFlex
      alignItems="center"
      gap="12px"
      flex="1"
      minWidth="0"
      cursor={onClick ? 'pointer' : undefined}
      onClick={onClick}
      _hover={onClick ? { '& [data-testid^="patient-name-"]': { textDecoration: 'underline' } } : undefined}
    >
      <Avatar name={fullName} size="xs" userType="patient" />
      <ChakraBox flex="1" minWidth="0">
        <PatientNameLine
          fullName={fullName}
          patientExternalId={patientExternalId}
          pronounsDisplay={pronounsDisplay}
          age={age}
        />
        <PatientMetaLine
          level={level}
          weekAtEquip={weekAtEquip}
          diagnosis={diagnosis}
          treatmentModality={treatmentModality}
          programName={programName}
        />
        {footer}
      </ChakraBox>
    </ChakraFlex>
  );
};

export default PatientIdentity;
